import React from "react";
import { isTruthy } from "@remi/ts-utils";

import { useMedia } from "../hooks";
import { cn } from "../lib/utils";
import {
  DropdownCommand,
  DropdownCommandGroup,
  DropdownCommandItem,
  DropdownCommandSeparator,
} from "./core/DropdownCommand";
import { SelectItem } from "./core/SelectItem";
import { RemiCalendar, type RemiCalendarProps } from "./RemiCalendar";
import { RemiCollapsible, RemiCollapsibleContent } from "./RemiCollapsible";

type RemiSelectDateProps = RemiCalendarProps & {
  actions?: React.ReactNode;
  reset?: () => void;
  resetLabel?: string;
  setOpen?: (open: boolean) => void;
};

function RemiSelectDate(props: RemiSelectDateProps) {
  const { reset, resetLabel = "Reset", actions, setOpen } = props;
  const { isMobile } = useMedia();

  const hasValue = () => {
    if (props.mode === "single") {
      return props.selected !== undefined;
    } else if (props.mode === "range") {
      return props.selected?.from !== undefined || props.selected?.to !== undefined;
    } else {
      return props.selected?.length !== 0;
    }
  };

  let dateSelect = <RemiCalendar {...props} />;

  if (props.mode === "single") {
    dateSelect = (
      <RemiCalendar
        {...props}
        mode="single"
        onSelect={(date, ...args) => {
          if (date) {
            date.setHours(12, 0, 0, 0);
          }
          props.onSelect?.(date, ...args);
          setOpen?.(false);
        }}
      />
    );
  }

  if (props.mode === "range") {
    dateSelect = (
      <RemiCalendar
        {...props}
        mode="range"
        onSelect={(range, ...args) => {
          if (range) {
            if (range.from) {
              range.from.setHours(12, 0, 0, 0);
            }
            if (range.to) {
              range.to.setHours(12, 0, 0, 0);
            }
          }
          props.onSelect?.(range, ...args);
        }}
      />
    );
  }

  if (props.mode === "multiple") {
    dateSelect = (
      <RemiCalendar
        {...props}
        mode="multiple"
        onSelect={(dates, ...args) => {
          if (dates) {
            dates.forEach((date) => date.setHours(12, 0, 0, 0));
          }
          props.onSelect?.(dates, ...args);
        }}
      />
    );
  }

  return (
    <DropdownCommand
      className={cn("bg-background-primary", isMobile ? "flex w-full items-center rounded-b-none" : "w-fit")}
    >
      <DropdownCommandItem className={cn("m-2 flex items-center justify-center", isMobile && "w-full")}>
        {dateSelect}
      </DropdownCommandItem>
      <RemiCollapsible open={(reset !== undefined && hasValue()) || actions !== undefined}>
        <RemiCollapsibleContent>
          <RemiCollapsible open={actions !== undefined}>
            <RemiCollapsibleContent>
              {isTruthy(actions) && (
                <>
                  <DropdownCommandSeparator />
                  <DropdownCommandGroup className="w-full">{actions}</DropdownCommandGroup>
                </>
              )}
            </RemiCollapsibleContent>
          </RemiCollapsible>
          <RemiCollapsible open={reset !== undefined && hasValue()}>
            <RemiCollapsibleContent>
              {reset !== undefined && hasValue() && (
                <>
                  <DropdownCommandSeparator />
                  <DropdownCommandGroup className="w-full">
                    <SelectItem onSelect={() => reset()} variant="destructive" className="justify-center">
                      {resetLabel}
                    </SelectItem>
                  </DropdownCommandGroup>
                </>
              )}
            </RemiCollapsibleContent>
          </RemiCollapsible>
        </RemiCollapsibleContent>
      </RemiCollapsible>
    </DropdownCommand>
  );
}

export { RemiSelectDate };
